import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useLocation } from 'wouter';
import { useTranslation } from 'react-i18next';
import { format, parseISO, addDays, startOfWeek, isSameDay, isToday } from 'date-fns';
import { getDateLocale, getDateFormats } from '@/utils/dateLocale';
import { Calendar, Clock, User, AlertCircle, CheckCircle, ChevronLeft, ChevronRight, Loader2, ArrowRight } from 'lucide-react';

import apiClient from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { LanguagePill } from '@/components/chrome/LanguagePill';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface GuestBooking {
  id: number;
  status: string;
  customer_first_name: string;
  customer_last_name: string;
  customer_full_name: string;
  customer_email: string;
  customer_phone: string;
  notes: string;
  start_time: string;
  end_time: string;
  service: {
    id: number;
    name: string;
    description: string;
    duration_minutes: number;
    price: number;
  };
  provider: {
    id: number;
    name: string;
    title: string;
    bio: string;
  };
  organization: {
    id: number;
    name: string;
    email: string;
    phone: string;
    address: string;
  };
  token_expires_at: string;
}

interface GuestBookingResponse {
  booking: GuestBooking;
  can_cancel: boolean;
  can_reschedule: boolean;
}

interface AvailableSlot {
  id: number;
  start_time: string;
  end_time: string;
  provider_id: number;
  provider_name: string;
  available: boolean;
  is_full?: boolean;
  waitlist_enabled?: boolean;
  spots_remaining?: number;
}

interface RescheduleOptionsResponse {
  available_slots: AvailableSlot[];
}

// A full session can still be offered when its class has a waitlist — selecting
// it cancels the current booking and joins the new session's waitlist.
const isWaitlistSlot = (slot: AvailableSlot) => !!slot.is_full && !!slot.waitlist_enabled;
const isSelectableSlot = (slot: AvailableSlot) => slot.available || isWaitlistSlot(slot);

export default function GuestBookingReschedulePage() {
  const { token } = useParams<{ token: string }>();
  const [_, setLocation] = useLocation();
  const { t } = useTranslation('guestBooking');
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [currentWeekStart, setCurrentWeekStart] = useState<Date>(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [selectedSlot, setSelectedSlot] = useState<AvailableSlot | null>(null);
  const [rescheduleReason, setRescheduleReason] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [waitlisted, setWaitlisted] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [datesWithSlots, setDatesWithSlots] = useState<Set<string>>(new Set());
  const [hasAutoSelectedDate, setHasAutoSelectedDate] = useState(false);
  const actionRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();

  // Fetch booking details
  const { data: bookingData, isLoading: isLoadingBooking, error: bookingError } = useQuery<GuestBookingResponse>({
    queryKey: ['guest-booking', token],
    queryFn: async () => {
      const response = await apiClient.get(`/v1/guest/bookings/${token}`);
      return response.data.data;
    },
    enabled: !!token,
    retry: false,
  });

  // Fetch available slots for rescheduling
  const { data: slotsData, isLoading: isLoadingSlots } = useQuery<RescheduleOptionsResponse>({
    queryKey: ['guest-reschedule-options', token, format(selectedDate, 'yyyy-MM-dd')],
    queryFn: async () => {
      const response = await apiClient.get(`/v1/guest/bookings/${token}/reschedule-options`, {
        params: {
          date: format(selectedDate, 'yyyy-MM-dd'),
        },
      });
      return response.data.data;
    },
    enabled: !!token && !!bookingData?.can_reschedule,
    retry: false,
  });

  // Fetch week slots to mark available dates
  const { data: weekSlotsData } = useQuery({
    queryKey: ['guest-reschedule-week', token, format(currentWeekStart, 'yyyy-MM-dd')],
    queryFn: async () => {
      const weekEnd = addDays(currentWeekStart, 6);
      const response = await apiClient.get(`/v1/guest/bookings/${token}/reschedule-options`, {
        params: {
          start_date: format(currentWeekStart, 'yyyy-MM-dd'),
          end_date: format(weekEnd, 'yyyy-MM-dd'),
        },
      });
      return response.data.data;
    },
    enabled: !!token && !!bookingData?.can_reschedule,
  });

  // Update dates with slots when week data changes
  useEffect(() => {
    if (weekSlotsData?.available_slots) {
      const datesSet = new Set<string>();
      weekSlotsData.available_slots.forEach((slot: AvailableSlot) => {
        if (isSelectableSlot(slot)) {
          const slotDate = parseISO(slot.start_time);
          datesSet.add(format(slotDate, 'yyyy-MM-dd'));
        }
      });
      setDatesWithSlots(datesSet);

      // Auto-select the earliest date that actually has slots so the customer
      // lands on the next bookable day instead of an empty today. Runs once per
      // week view (until a date is picked); if this week is empty it fires again
      // when the customer pages to a week that has slots.
      if (!hasAutoSelectedDate && datesSet.size > 0) {
        const earliest = Array.from(datesSet).sort()[0];
        setSelectedDate(parseISO(earliest));
        setHasAutoSelectedDate(true);
      }
    }
  }, [weekSlotsData, hasAutoSelectedDate]);

  // After picking a slot, bring the confirm panel into view — on mobile it
  // renders below the picker, so a tap otherwise leaves the action off-screen.
  useEffect(() => {
    if (selectedSlot) {
      actionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [selectedSlot]);

  // Reschedule mutation
  const rescheduleMutation = useMutation({
    mutationFn: async () => {
      if (!selectedSlot) throw new Error('No slot selected');
      const response = await apiClient.post(`/v1/guest/bookings/${token}/reschedule`, {
        start_time: selectedSlot.start_time,
        reason: rescheduleReason || 'Customer requested reschedule',
      });
      return response.data.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['guest-booking', token] });
      setWaitlisted(!!data?.waitlisted);
      setShowSuccess(true);
    },
  });

  const handleReschedule = () => {
    setShowConfirmDialog(true);
  };

  const handleConfirmReschedule = () => {
    setShowConfirmDialog(false);
    rescheduleMutation.mutate();
  };

  // The week containing today — never let the pager go before it, since past
  // sessions can't be rescheduled into (the backend rejects them anyway).
  const thisWeekStart = startOfWeek(new Date(), { weekStartsOn: 1 });
  const canGoPrev = currentWeekStart > thisWeekStart;

  const navigateWeek = (direction: 'prev' | 'next') => {
    if (direction === 'prev' && !canGoPrev) return;
    const newWeekStart = direction === 'prev'
      ? addDays(currentWeekStart, -7)
      : addDays(currentWeekStart, 7);
    setCurrentWeekStart(newWeekStart);
    // Set selected date to the first day of the new week (today, if it's this week)
    setSelectedDate(newWeekStart < thisWeekStart ? new Date() : newWeekStart);
  };

  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(currentWeekStart, i));

  if (isLoadingBooking) {
    return (
      <div className="min-h-screen bg-surface-2 flex items-center justify-center p-4">
        <Card className="w-full max-w-2xl">
          <CardContent className="pt-6">
            <div className="text-center space-y-4">
              <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto" />
              <p className="text-soft">{t('reschedule.loading')}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (bookingError || !bookingData) {
    return (
      <div className="min-h-screen bg-surface-2 flex items-center justify-center p-4">
        <Card className="w-full max-w-2xl">
          <CardContent className="pt-6">
            <div className="text-center space-y-4">
              <AlertCircle className="h-12 w-12 text-red-500 mx-auto" />
              <div>
                <h3 className="text-h3 text-ink">{t('reschedule.bookingNotFound')}</h3>
                <p className="text-soft">
                  {t('reschedule.bookingNotFoundDescription')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { booking, can_reschedule } = bookingData;
  const originalStartTime = parseISO(booking.start_time);

  // Show success state after rescheduling
  if (showSuccess) {
    return (
      <div className="min-h-screen bg-surface-2 py-8 px-4">
        <div className="max-w-3xl mx-auto space-y-6">
          <div className="flex justify-end">
            <LanguagePill />
          </div>
          <div className="text-center space-y-2">
            <h1 className="text-h1 text-ink">{booking.organization.name}</h1>
            <p className="text-soft">{waitlisted ? t('reschedule.waitlistedTitle') : t('reschedule.appointmentRescheduled')}</p>
          </div>

          <Card>
            <CardContent className="pt-6">
              <div className="text-center space-y-4">
                <CheckCircle className={`h-16 w-16 mx-auto ${waitlisted ? 'text-amber-500' : 'text-green-500'}`} />
                <div>
                  <h2 className="text-h1 text-ink mb-2">{waitlisted ? t('reschedule.waitlistSuccessTitle') : t('reschedule.successTitle')}</h2>
                  <p className="text-soft">
                    {waitlisted ? t('reschedule.waitlistSuccessDescription') : t('reschedule.successDescription')}
                  </p>
                </div>

                <Separator className="my-6" />

                {selectedSlot && (
                  <div className="text-left space-y-3">
                    <h3 className="font-semibold">{waitlisted ? t('reschedule.waitlistedClassDetails') : t('reschedule.newAppointmentDetails')}</h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-soft" />
                        <span>{format(parseISO(selectedSlot.start_time), getDateFormats().fullWithWeekday, { locale: getDateLocale() })}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-soft" />
                        <span>{format(parseISO(selectedSlot.start_time), 'HH:mm')}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-soft" />
                        <span>{t('reschedule.serviceWith', { service: booking.service.name, provider: selectedSlot.provider_name })}</span>
                      </div>
                    </div>
                  </div>
                )}

                <Separator className="my-6" />

                <Button
                  onClick={() => setLocation(`/guest/bookings/${token}`)}
                  className="w-full"
                >
                  {t('reschedule.viewUpdatedBooking')}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Show cannot reschedule message
  if (!can_reschedule) {
    return (
      <div className="min-h-screen bg-surface-2 py-8 px-4">
        <div className="max-w-3xl mx-auto space-y-6">
          <div className="flex justify-end">
            <LanguagePill />
          </div>
          <div className="text-center space-y-2">
            <h1 className="text-h1 text-ink">{booking.organization.name}</h1>
            <p className="text-soft">{t('reschedule.title')}</p>
          </div>

          <Card>
            <CardContent className="pt-6">
              <div className="text-center space-y-4">
                <AlertCircle className="h-16 w-16 text-amber-500 mx-auto" />
                <div>
                  <h2 className="text-h1 text-ink mb-2">{t('reschedule.cannotReschedule')}</h2>
                  <p className="text-soft">
                    {t('reschedule.cannotRescheduleDescription')}
                  </p>
                </div>

                <Separator className="my-6" />

                <div className="space-y-3">
                  <p className="text-sm text-soft">
                    {t('reschedule.contactAssistance')}
                  </p>
                  <div className="space-y-2">
                    <Button
                      onClick={() => setLocation(`/guest/bookings/${token}`)}
                      className="w-full"
                    >
                      {t('reschedule.backToBookingDetails')}
                    </Button>
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => window.location.href = `mailto:${booking.organization.email}`}
                    >
                      {t('reschedule.contact', { name: booking.organization.name })}
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const selectedIsWaitlist = selectedSlot ? isWaitlistSlot(selectedSlot) : false;
  const availableSlots = slotsData?.available_slots?.filter(isSelectableSlot) || [];
  const slotsForSelectedDate = availableSlots.filter(slot => {
    const slotDate = parseISO(slot.start_time);
    return isSameDay(slotDate, selectedDate);
  });

  // Show reschedule form
  return (
    <div className="min-h-screen bg-surface-2 py-8 px-4">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex justify-end">
          <LanguagePill />
        </div>
        <div className="text-center space-y-2">
          <h1 className="text-h1 text-ink">{booking.organization.name}</h1>
          <p className="text-soft">{t('reschedule.title')}</p>
        </div>

        <Alert className="border-blue-200 bg-blue-50">
          <AlertCircle className="h-4 w-4 text-blue-600" />
          <AlertDescription className="text-blue-800">
            {t('reschedule.selectNewDateTimeHint')}
          </AlertDescription>
        </Alert>

        <div className="grid gap-6 lg:grid-cols-2">
          {/* Current Booking Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">{t('reschedule.currentAppointment')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3 p-4 bg-surface-2 rounded-lg">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-soft" />
                  <span className="text-sm">{format(originalStartTime, getDateFormats().fullWithWeekday, { locale: getDateLocale() })}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-soft" />
                  <span className="text-sm">{format(originalStartTime, 'HH:mm')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-soft" />
                  <span className="text-sm">{booking.service.name}</span>
                </div>
                <div className="text-xs text-soft">
                  {t('reschedule.providerLabel', { name: booking.provider.name })}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Available Times Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">{t('reschedule.selectNewDateTime')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Week Navigation */}
              <div className="flex items-center justify-between mb-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigateWeek('prev')}
                  disabled={!canGoPrev}
                  aria-label={t('reschedule.previousWeek')}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm font-medium">
                  {format(currentWeekStart, getDateFormats().dayMonth, { locale: getDateLocale() })} - {format(addDays(currentWeekStart, 6), getDateFormats().medium, { locale: getDateLocale() })}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigateWeek('next')}
                  aria-label={t('reschedule.nextWeek')}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>

              {/* Week Day Selector */}
              <div className="grid grid-cols-7 gap-1 mb-4">
                {weekDays.map((day) => {
                  const dayStr = format(day, 'yyyy-MM-dd');
                  const hasSlots = datesWithSlots.has(dayStr);
                  const isSelected = isSameDay(day, selectedDate);
                  const isPast = day < new Date() && !isToday(day);

                  return (
                    <button
                      key={dayStr}
                      onClick={() => setSelectedDate(day)}
                      disabled={!hasSlots || isPast}
                      className={`
                        p-2 rounded-lg text-center transition-colors
                        ${isSelected ? 'bg-primary text-primary-foreground' : ''}
                        ${hasSlots && !isSelected ? 'bg-green-50 hover:bg-green-100 text-green-900' : ''}
                        ${!hasSlots || isPast ? 'bg-surface-2 text-soft cursor-not-allowed' : ''}
                      `}
                    >
                      <div className="text-xs font-medium">{format(day, 'EEE', { locale: getDateLocale() })}</div>
                      <div className="text-sm">{format(day, 'd', { locale: getDateLocale() })}</div>
                      {hasSlots && !isPast && (
                        <div className="w-1 h-1 bg-green-500 rounded-full mx-auto mt-1"></div>
                      )}
                    </button>
                  );
                })}
              </div>

              <Separator />

              {/* Available Slots */}
              <div>
                <h4 className="text-sm font-semibold mb-3">
                  {t('reschedule.availableTimes', { date: format(selectedDate, getDateFormats().full, { locale: getDateLocale() }) })}
                </h4>

                {isLoadingSlots ? (
                  <div className="text-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-2" />
                    <p className="text-sm text-soft">{t('reschedule.loadingAvailableTimes')}</p>
                  </div>
                ) : slotsForSelectedDate.length === 0 ? (
                  <div className="text-center py-8">
                    <AlertCircle className="h-8 w-8 text-soft mx-auto mb-2" />
                    <p className="text-sm text-soft">
                      {t('reschedule.noAvailableTimes')}
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-2 max-h-80 overflow-y-auto">
                    {slotsForSelectedDate.map((slot, index) => {
                      const slotTime = parseISO(slot.start_time);
                      const isSelected = selectedSlot?.start_time === slot.start_time;
                      const waitlistSlot = isWaitlistSlot(slot);

                      return (
                        <button
                          key={slot.id || `slot-${slot.start_time}-${index}`}
                          onClick={() => setSelectedSlot(slot)}
                          className={`
                            p-3 rounded-lg border text-left transition-all
                            ${isSelected
                              ? 'border-primary bg-primary text-primary-foreground'
                              : 'border-line hover:border-primary hover:bg-surface-2'
                            }
                          `}
                        >
                          <div className="font-medium text-sm">
                            {format(slotTime, 'HH:mm')}
                          </div>
                          <div className="text-xs opacity-80">
                            {slot.provider_name}
                          </div>
                          {waitlistSlot && (
                            <div className={`mt-1 inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium ${
                              isSelected ? 'bg-primary-foreground/20 text-primary-foreground' : 'bg-amber-100 text-amber-800'
                            }`}>
                              {t('reschedule.waitlistBadge')}
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Selected-slot action — full width, rendered below the picker so on
            mobile it appears right where the customer just tapped. */}
        {selectedSlot && (
          <Card ref={actionRef}>
            <CardHeader>
              <div className="flex items-center gap-2">
                <ArrowRight className="h-4 w-4 text-primary" />
                <CardTitle className="text-lg">{selectedIsWaitlist ? t('reschedule.joinWaitlistFor') : t('reschedule.newAppointment')}</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {selectedIsWaitlist && (
                <Alert className="border-amber-200 bg-amber-50">
                  <AlertCircle className="h-4 w-4 text-amber-600" />
                  <AlertDescription className="text-amber-800">
                    {t('reschedule.waitlistNotice')}
                  </AlertDescription>
                </Alert>
              )}
              <div className={`space-y-3 p-4 border rounded-lg ${selectedIsWaitlist ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'}`}>
                <div className="flex items-center gap-2">
                  <Calendar className={`h-4 w-4 ${selectedIsWaitlist ? 'text-amber-700' : 'text-green-700'}`} />
                  <span className={`text-sm ${selectedIsWaitlist ? 'text-amber-900' : 'text-green-900'}`}>
                    {format(parseISO(selectedSlot.start_time), getDateFormats().fullWithWeekday, { locale: getDateLocale() })}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className={`h-4 w-4 ${selectedIsWaitlist ? 'text-amber-700' : 'text-green-700'}`} />
                  <span className={`text-sm ${selectedIsWaitlist ? 'text-amber-900' : 'text-green-900'}`}>
                    {format(parseISO(selectedSlot.start_time), 'HH:mm')}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <User className={`h-4 w-4 ${selectedIsWaitlist ? 'text-amber-700' : 'text-green-700'}`} />
                  <span className={`text-sm ${selectedIsWaitlist ? 'text-amber-900' : 'text-green-900'}`}>{selectedSlot.provider_name}</span>
                </div>
              </div>

              {!selectedIsWaitlist && (
                <div className="space-y-2">
                  <Label htmlFor="reason">{t('reschedule.reasonLabel')}</Label>
                  <Textarea
                    id="reason"
                    placeholder={t('reschedule.reasonPlaceholder')}
                    value={rescheduleReason}
                    onChange={(e) => setRescheduleReason(e.target.value)}
                    rows={3}
                    className="resize-none"
                  />
                </div>
              )}

              <Button
                onClick={handleReschedule}
                disabled={rescheduleMutation.isPending}
                className="w-full"
              >
                {rescheduleMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {t('reschedule.rescheduling')}
                  </>
                ) : (
                  selectedIsWaitlist ? t('reschedule.confirmJoinWaitlist') : t('reschedule.confirmReschedule')
                )}
              </Button>

              {rescheduleMutation.isError && (
                <Alert className="border-red-200 bg-red-50">
                  <AlertCircle className="h-4 w-4 text-red-600" />
                  <AlertDescription className="text-red-800">
                    {t('reschedule.rescheduleError')}
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>
        )}

        <div className="flex justify-center">
          <Button
            variant="outline"
            onClick={() => setLocation(`/guest/bookings/${token}`)}
          >
            {t('reschedule.cancelBackToBooking')}
          </Button>
        </div>
      </div>

      {/* Confirm Reschedule Dialog */}
      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{selectedIsWaitlist ? t('reschedule.confirmWaitlistDialogTitle') : t('reschedule.confirmDialogTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {selectedIsWaitlist ? t('reschedule.confirmWaitlistDialogDescription') : t('reschedule.confirmDialogDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('reschedule.cancelBackToBooking')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmReschedule}>
              {selectedIsWaitlist ? t('reschedule.confirmJoinWaitlist') : t('reschedule.confirmReschedule')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
